/**
 * Live tool-call timeline for Cortex drawer.
 * Visual sibling of SubAgentVisualization — compact lanes for each tool.
 */

import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { CheckCircle2, XCircle, Loader2, Wrench } from 'lucide-react';
import { cn } from '@flowstate/utils';
import type { ToolActivity, ToolActivityEntry } from '../types/chat';

interface ToolActivityTimelineProps {
  activity: ToolActivity;
  /** When true, hide the outer header chrome (for nested WorkTrail). */
  compact?: boolean;
}

function StatusIcon({ status }: { status: ToolActivityEntry['status'] }) {
  switch (status) {
    case 'running':
      return (
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
        >
          <Loader2 className="h-3.5 w-3.5 text-primary" />
        </motion.div>
      );
    case 'completed':
      return <CheckCircle2 className="h-3.5 w-3.5 text-green-500 dark:text-green-400" />;
    case 'failed':
      return <XCircle className="h-3.5 w-3.5 text-red-500 dark:text-red-400" />;
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

function formatDuration(ms?: number): string | null {
  if (ms == null) return null;
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

const ToolLane: React.FC<{ entry: ToolActivityEntry; index: number }> = ({ entry, index }) => {
  const durationLabel = formatDuration(entry.durationMs);

  return (
    <motion.div
      initial={{ opacity: 0, x: -12, height: 0 }}
      animate={{ opacity: 1, x: 0, height: 'auto' }}
      exit={{ opacity: 0, x: 8, height: 0 }}
      transition={{ duration: 0.25, delay: index * 0.05 }}
      className="overflow-hidden"
    >
      <div
        className={cn(
          'relative flex items-start gap-2.5 px-3 py-2 rounded-lg transition-colors duration-200',
          entry.status === 'running' && 'bg-primary/8 dark:bg-primary/12',
          entry.status === 'completed' && 'bg-green-500/8 dark:bg-green-400/12',
          entry.status === 'failed' && 'bg-red-500/8 dark:bg-red-400/12',
        )}
      >
        <div
          className={cn(
            'flex-shrink-0 p-1 rounded-md mt-0.5',
            entry.status === 'running' && 'text-primary',
            entry.status === 'completed' && 'text-green-600 dark:text-green-400',
            entry.status === 'failed' && 'text-red-600 dark:text-red-400',
          )}
        >
          <Wrench className="h-3.5 w-3.5" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'text-xs font-medium truncate',
                entry.status === 'running' && 'text-primary',
                entry.status === 'completed' && 'text-green-700 dark:text-green-300',
                entry.status === 'failed' && 'text-red-700 dark:text-red-300',
              )}
            >
              {entry.tool}
            </span>
            {durationLabel && entry.status !== 'running' && (
              <span className="text-[10px] text-muted-foreground tabular-nums">{durationLabel}</span>
            )}
          </div>
          {entry.message && (
            <p className="text-[10px] text-muted-foreground mt-0.5 line-clamp-2 break-words">
              {entry.message}
            </p>
          )}
        </div>

        <StatusIcon status={entry.status} />

        {entry.status === 'running' && (
          <div className="absolute bottom-0 left-0 right-0 h-0.5 overflow-hidden rounded-full">
            <motion.div
              className="h-full bg-primary/40"
              animate={{ x: ['-100%', '100%'] }}
              transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
            />
          </div>
        )}
      </div>
    </motion.div>
  );
};

export const ToolActivityTimeline: React.FC<ToolActivityTimelineProps> = ({
  activity,
  compact = false,
}) => {
  const entries = activity.entries ?? [];
  if (entries.length === 0) return null;

  const running = entries.filter((e) => e.status === 'running').length;
  const done = entries.filter((e) => e.status === 'completed' || e.status === 'failed').length;

  const lanes = (
    <div className={cn(compact ? 'space-y-1' : 'p-1.5 space-y-1')}>
      <AnimatePresence mode="sync">
        {entries.map((entry, idx) => (
          <ToolLane key={entry.id} entry={entry} index={idx} />
        ))}
      </AnimatePresence>
    </div>
  );

  if (compact) {
    return <div className="w-full">{lanes}</div>;
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.98 }}
      transition={{ duration: 0.2 }}
      className="w-full"
    >
      <div className="rounded-xl border border-primary/15 bg-primary/[0.03] overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-2 border-b border-primary/10">
          <Wrench className="h-3.5 w-3.5 text-primary/70" />
          <span className="text-xs font-semibold text-primary/80">
            {running > 0
              ? `Running ${running} tool${running === 1 ? '' : 's'}`
              : `${done} tool${done === 1 ? '' : 's'} completed`}
          </span>
        </div>
        {lanes}
      </div>
    </motion.div>
  );
};

export default ToolActivityTimeline;
